"use client";

import React, { useEffect, useRef, useState } from "react";
import { Leaf, Droplets, TreeDeciduous } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const varieties = [{
  name: "Picual",
  description: "Carácter y estabilidad, con notas intensas y un ligero picor característico que refleja la fuerza de nuestra tierra.",
  icon: Leaf
}, {
  name: "Hojiblanca",
  description: "Equilibrio perfecto, aromas a hierba fresca y un final suave que aporta versatilidad a nuestro aceite.",
  icon: Droplets
}, {
  name: "Lucio",
  description: "Variedad autóctona y centenaria, aportando una elegancia única y un legado histórico a nuestro coupage.",
  icon: TreeDeciduous
}];
export default function NuestraHistoria() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLHeadingElement>();
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: boxRef,
    isVisible: isBoxVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="nuestrahistoria" className="w-full flex flex-col">
      {}
      <div className="bg-background py-12 md:py-9 lg:py-12">
        <div className="container max-w-6xl mx-auto px-4">
          <h1 ref={headerRef} className={`text-center mb-20 text-foreground transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            Nuestra Historia
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24 items-center">
            <div ref={textRef} className={`space-y-6 text-lg text-muted-foreground leading-relaxed transition-all duration-1000 delay-200 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <p>
                Aceite Paco y Herminia es una marca familiar de aceite de oliva virgen extra de alta calidad, elaborado con cuidado desde el olivar hasta la botella. Nuestro proyecto nace de la tradición, el respeto por la tierra y el compromiso inquebrantable con la calidad, la sostenibilidad y la trazabilidad.
              </p>
              <p>
                Generación tras generación, hemos cultivado nuestros olivos bajo el sol del Mediterráneo, preservando los métodos artesanales que garantizan la pureza de cada gota. Ofrecemos un aceite equilibrado y auténtico, pensado tanto para la cocina diaria como para los paladares más exigentes y amantes del buen producto.
              </p>
            </div>

            <div ref={imageRef} className={`transition-all duration-1000 delay-400 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
              <AspectRatio ratio={4 / 5} className="overflow-hidden rounded-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1552070/img/family_harvest_story.png" alt="Familia recolectando aceitunas en el olivar" className="object-cover w-full h-full hover:scale-105 transition-transform duration-1000" />
              </AspectRatio>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="relative w-full py-12 md:py-9 lg:py-12 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1552070/img/granada_landscape_moclin.png)]">
        {}
        <div className="absolute inset-0 bg-black/30"></div>

        <div className="relative container max-w-5xl mx-auto px-4">
          <div ref={boxRef} className={`bg-secondary p-12 md:p-24 rounded-lg text-center transition-all duration-1000 ease-out ${isBoxVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h2 className="mb-16 text-foreground">Nuestra Tierra: Moclín y Olivares</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
              {varieties.map((variety, i) => {
              const Icon = variety.icon;
              return <div key={variety.name} data-index={i} className="flex flex-col items-center">
                    <div className="mb-8 p-4 rounded-lg bg-primary/5">
                      <Icon className="w-12 h-12 text-primary stroke-[1]" />
                    </div>
                    <h3 className="text-2xl mb-4 text-foreground">{variety.name}</h3>
                    <p className="text-muted-foreground text-base leading-relaxed">
                      {variety.description}
                    </p>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}